import AppIcon from "../components/AppIcon";
import { call as webCall } from '../utils/appData';
import { useEffect, useRef, useState } from "react";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { replace, useLocation } from "react-router";
import { useUser } from "../contexts/UserContext";
import useMoveTo from "../constants/navigation";
import { ref, push, set, update } from "firebase/database";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { database, storage } from "../constants/firebase";
import MobileHeader from "../components/MobileHeader";
import { convertToWebP, uploadFileToBunny } from "../utils/utils";
import LoadingModal from "../components/LoadingModal";
import { ROUTES } from "../App";

const AddCourse = () => {
    const navigate = useMoveTo();
    const location = useLocation();
    const { currentUser } = useUser();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [currency, setCurrency] = useState("USD");
    const [form, setForm] = useState({
        title: "",
        price: "",
        coverImage: null,
        trailer: null, // ✅ NEW
        explainer: "",
        status: "Draft"
    });
    const coverInputRef = useRef(null);
    const [isCoverImage, setIsCoverImage] = useState('');
    const trailerInputRef = useRef(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [existingTrailer, setExistingTrailer] = useState(null);

    // Loading Modal State
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("Please wait while we save your course...");

    useEffect(() => {
        if (currentUser?.country === "South Africa") {
            setCurrency("ZAR");
        } else {
            setCurrency("USD");
        }
    }, [currentUser]);

    useEffect(() => {
        if (location.state?.course) {
            setExistingTrailer(location.state.course.trailerUrl || null);
            setForm({
                title: location.state.course.title,
                price: location.state.course.price,
                coverImage: null, // Reset cover image for edit
                trailer: null,
                explainer: location.state.course.explainer || "",
                trailerVideoId: location.state.course.trailerVideoId || null,
                status: location.state.course.status || "Draft"
            });
            setIsCoverImage(!!location.state.course.coverImageUrl);
        }
    }, [])

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm({
            ...form,
            [name]: name === "price" ? Number(value) : value
        });
    };

    const handleTrailerUpload = (e) => {
        setForm({ ...form, trailer: e.target.files[0] });
    };

    const triggerTrailerUpload = () => {
        trailerInputRef.current?.click();
    };

    const handleCoverUpload = (e) => {
        setForm({ ...form, coverImage: e.target.files[0] });
    };

    const triggerCoverUpload = () => {
        coverInputRef.current?.click();
    };

    const handleSave = async () => {
        if (!form.title) {
            window.showToast("Please fill all required fields", "error");
            return;
        }

        setIsSubmitting(true);
        setShowLoadingModal(true);
        setLoadingMessage("Saving course...");

        try {
            let coverImageUrl = null;
            let trailerUrl = null;
            let trailerVideoId = location.state?.course?.trailerVideoId || null;
            let trailerOrientation = location.state?.course?.trailerOrientation || null;
            let trailerWidth = location.state?.course?.trailerWidth || null;
            let trailerHeight = location.state?.course?.trailerHeight || null;

            // ✅ Upload cover image if selected
            if (form.coverImage) {
                setLoadingMessage("Converting image to WebP...");
                const webpFile = await convertToWebP(form.coverImage); // ✅ Convert first

                setLoadingMessage("Uploading cover image...");
                const storageReference = storageRef(
                    storage,
                    `covers/${Date.now()}_${webpFile.name}`
                );
                await uploadBytes(storageReference, webpFile);
                coverImageUrl = await getDownloadURL(storageReference);
            }

            if (form.trailer) {
                const bunnyResult = await uploadFileToBunny(form.trailer, "courses", `trailers/${Date.now()}_${form.trailer.name}`, (percent) => setUploadProgress(percent));
                if (bunnyResult.success) {
                    trailerVideoId = bunnyResult.videoId; // ✅ SAVE THIS
                    const playbackUrl = bunnyResult.playbackUrl;
                    trailerUrl = playbackUrl; // Save playback URL in Firebase DB
                    trailerOrientation = bunnyResult.videoOrientation;
                    trailerWidth = bunnyResult.videoWidth;
                    trailerHeight = bunnyResult.videoHeight;
                } else {
                    throw new Error("Video upload failed: " + bunnyResult.message);
                }
            }

            setLoadingMessage("Saving course details...");

            if (location.state?.course?.id) {
                // ✅ EDIT existing course
                const updates = {
                    title: form.title,
                    price: form.price,
                    currency: currency,
                    explainer: form.explainer,
                    status: form.status,
                    ...(coverImageUrl && { coverImageUrl }), // only update if new image uploaded
                    ...(trailerUrl && { trailerUrl }), // only update if new trailer uploaded
                    ...(trailerVideoId && { trailerVideoId }),
                    ...(form.trailer && { trailerOrientation, trailerWidth, trailerHeight }),
                };
                await update(ref(database, `Listings/${location.state.course.id}`), updates);
                window.showToast("Course updated successfully!", "success");
            } else {
                // ✅ ADD new course
                const newCourseRef = push(ref(database, "Listings"));
                const courseId = newCourseRef.key;

                await set(newCourseRef, {
                    id: courseId,
                    title: form.title,
                    price: parseFloat(form.price),
                    currency: currency,
                    explainer: form.explainer,
                    coverImageUrl: coverImageUrl || "",
                    trailerUrl: trailerUrl || "",
                    trailerVideoId: trailerVideoId || "",
                    trailerOrientation: trailerUrl ? trailerOrientation : null,
                    trailerWidth: trailerUrl ? trailerWidth : null,
                    trailerHeight: trailerUrl ? trailerHeight : null,
                    type: "course",
                    userId: currentUser.uid,
                    status: form.status,
                    createdAt: Date.now()
                });
                setForm({
                    title: "",
                    price: "",
                    coverImage: null,
                    trailer: null,
                    explainer: ""
                });
                window.showToast("Course saved successfully!", "success");
                navigate(`${ROUTES.MANAGE_CHAPTER}/${courseId}`, { replace: true });
            }
        } catch (error) {
            console.error("Error saving course:", error);
            window.showToast("Failed to save course.", "error");
        } finally {
            setIsSubmitting(false);
            setShowLoadingModal(false);
        }
    };

    const handleDeleteVideo = async () => {
        if (!location.state?.course?.id) return;

        const courseId = location.state.course.id;
        const videoId = location.state.course.trailerVideoId;

        if (!videoId) {
            window.showToast("No trailer to delete", "error");
            return;
        }

        try {
            setIsDeleting(true);
            await webCall('deleteWebVideo', { videoId, courseId });

            // 2️⃣ Remove trailer fields from Firebase
            await update(ref(database, `Listings/${courseId}`), {
                trailerUrl: null,
                trailerVideoId: null,
                trailerOrientation: null,
                trailerWidth: null,
                trailerHeight: null,
            });

            setExistingTrailer(null);
            setForm((prev) => ({
                ...prev,
                trailer: null,
                trailerVideoId: null,
            }));

            window.showToast("Trailer deleted successfully!", "success");

        } catch (err) {
            console.error("❌ Error deleting trailer:", err);
            window.showToast("Failed to delete trailer", "error");
        } finally {
            setIsDeleting(false);
        }
    };


    return (
        <div className="h-screen bg-white text-black dark:bg-[#23272c] dark:text-white overflow-y-auto">
            <MobileHeader title={location.state?.course ? "Edit Course" : "Add Course"} isBackBtn={true} />

            {/* Loading Modal */}
            <LoadingModal showLoadingModal={showLoadingModal} loadingMessage={loadingMessage} />

            <div className="flex">
                <Sidebar />
                <main className="flex-1 h-screen pt-5 pb-24 md:pb-20 px-6 overflow-y-auto bg-white text-black dark:bg-[#23272c] dark:text-white">
                    <div className="flex items-center justify-between">
                        <button onClick={() => navigate(-1)} className="flex-shrink-0 md:block hidden">
                            <AppIcon name="arrowBack" className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert" alt="Back" />
                        </button>
                        <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">{location.state?.course ? "Edit Course" : "Add Course"}</h2>
                        <div />
                    </div>
                    <div className="max-w-md mx-auto md:pt-10 pt-3 space-y-6">
                        {/* Title */}
                        <input
                            type="text"
                            name="title"
                            placeholder="Title"
                            value={form.title}
                            onChange={handleChange}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] bg-[#F5F9FE] dark:bg-[#F5F9FE] text-black dark:text-black border border-gray-200 dark:border-0"
                        />
                        {/* Price */}
                        <input
                            type="number"
                            name="price"
                            placeholder={`Price (${currency})`}
                            value={form.price}
                            onChange={handleChange}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] bg-[#F5F9FE] dark:bg-[#F5F9FE] text-black dark:text-black border border-gray-200 dark:border-0"
                        />
                        {/* Cover Image Upload */}
                        <div className="w-full cursor-pointer border-dashed border-2 border-gray-400 p-6 text-center rounded-xl bg-[#F5F9FE] dark:bg-white text-[#7C8BA0]" onClick={triggerCoverUpload}>
                            <input
                                ref={coverInputRef}
                                type="file"
                                accept="image/png,image/jpg,image/jpeg"
                                className="hidden"
                                onChange={handleCoverUpload}
                            />
                            <AppIcon name="upload" className="mx-auto mb-2 w-8 h-8" alt="Upload" />
                            <p className="font-semibold text-base text-[#7C8BA0]">Upload the Product cover image:</p>
                            <p className="text-xs text-[#7C8BA0]">Use 16:9 for best results; other images will be auto-cropped (.png, .jpg, .jpeg).</p>
                            {form.coverImage && (
                                <p className="mt-2 text-xs text-green-600">
                                    Selected: {form.coverImage.name}
                                </p>
                            )}

                            {!form.coverImage && isCoverImage && (
                                <p className="mt-2 text-xs text-green-600">
                                    Cover image already exists
                                </p>
                            )}
                        </div>
                        {/* Trailer Upload */}
                        <div
                            className="w-full cursor-pointer border-dashed border-2 border-gray-400 p-6 text-center rounded-xl bg-[#F5F9FE] dark:bg-white text-[#7C8BA0]"
                            onClick={triggerTrailerUpload}
                        >
                            <input
                                ref={trailerInputRef}
                                type="file"
                                accept="video/mp4,video/webm,video/ogg"
                                className="hidden"
                                onChange={handleTrailerUpload}
                            />

                            <AppIcon name="upload" className="mx-auto mb-2 w-8 h-8" alt="Upload" />
                            <p className="font-semibold text-base">Upload Course Trailer (Optional)</p>
                            <p className="text-xs">Recommended: MP4 format, max 50MB</p>

                            {form.trailer && (
                                <p className="mt-2 text-xs text-green-600">
                                    Selected: {form.trailer.name}
                                </p>
                            )}

                            {!form.trailer && existingTrailer && (
                                <p className="mt-2 text-xs text-green-600">
                                    Trailer already uploaded
                                </p>
                            )}
                        </div>
                        <p className="text-xs text-[#7C8BA0] dark:text-white/60">
                            Trailer layout is detected automatically from the uploaded video dimensions so portrait Bunny videos open correctly for users.
                        </p>
                        {existingTrailer && !form.trailer && (
                            <button
                                onClick={handleDeleteVideo}
                                disabled={isSubmitting || isDeleting}
                                className="w-full bg-red-600 text-white py-2 rounded-xl font-medium"
                            >
                                {isDeleting ? "Deleting Trailer..." : "Delete Trailer Video"}
                            </button>
                        )}
                        {/* Explainer Text */}
                        <input
                            type="text"
                            name="explainer"
                            placeholder="Explainer Text"
                            value={form.explainer}
                            onChange={handleChange}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] bg-[#F5F9FE] dark:bg-[#F5F9FE] text-black dark:text-black border border-gray-200 dark:border-0"
                        />
                        {/* Status */}
                        <select
                            name="status"
                            value={form.status}
                            onChange={handleChange}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0 appearance-none bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJsdWNpZGUgbHVjaWRlLWNoZXZyb24tZG93biI+PHBhdGggZD0ibTYgOSA2IDYgNi02Ii8+PC9zdmc+')] bg-no-repeat bg-[position:right_0.5rem_center] bg-[size:1.5em_1.5em]"
                        >
                            <option value="Draft">Draft</option>
                            <option value="Published">Published</option>
                        </select>
                        {/* Save Button */}
                        <button
                            onClick={handleSave}
                            className="bg-[#327691] text-white text-lg font-semibold py-3 rounded-xl w-full mt-4"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? uploadProgress > 0
                                ? `Uploading Video… ${uploadProgress}%` : "Saving..." : "Save Course"}
                        </button>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AddCourse;